import { getToolName, isToolUIPart, type UIMessage } from "ai";

export type DraftClauseLibraryProposalInput = {
  id?: string;
  template_id?: string;
  section_key?: string;
  clause_category?: string | null;
  posture?: string | null;
  language?: string;
  jurisdiction_applicability?: string;
  version?: string;
};

export type ToolCallApprovalPayload = {
  runId: string;
  toolCallId: string;
  toolName: string;
  args: DraftClauseLibraryProposalInput;
};

export type DraftClauseLibraryToolOutput = {
  success?: boolean;
  draftClauseAlternative?: {
    id: string;
    section_key?: string;
    clause_category?: string | null;
    language?: string;
  };
  error?: string;
};

export const WRITE_TOOL_NAMES = new Set([
  "createDraftClauseAlternative",
  "updateDraftClauseAlternative",
  "create-draft-clause-alternative",
  "update-draft-clause-alternative",
]);

export function isWriteToolName(toolName: string): boolean {
  return WRITE_TOOL_NAMES.has(toolName);
}

export function findSuccessfulDraftClauseLibraryWrite(
  messages: UIMessage[]
): { toolCallId: string; clauseId: string; label: string } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== "assistant") continue;
    for (let j = message.parts.length - 1; j >= 0; j--) {
      const part = message.parts[j];
      if (!isToolUIPart(part)) continue;
      if (!isWriteToolName(getToolName(part))) continue;
      if (part.state !== "output-available") continue;
      const output = part.output as DraftClauseLibraryToolOutput | undefined;
      if (output?.success !== true || !output.draftClauseAlternative?.id) continue;
      const alt = output.draftClauseAlternative;
      const label =
        alt.section_key ??
        alt.clause_category ??
        alt.language?.slice(0, 48) ??
        "Clause variant";
      return {
        toolCallId: part.toolCallId,
        clauseId: alt.id,
        label,
      };
    }
  }
  return null;
}

export function normalizeProposalInput(raw: unknown): DraftClauseLibraryProposalInput {
  if (!raw || typeof raw !== "object") return {};
  const o = raw as Record<string, unknown>;
  return {
    id: typeof o.id === "string" ? o.id : undefined,
    template_id:
      typeof o.template_id === "string"
        ? o.template_id
        : typeof o.templateId === "string"
          ? o.templateId
          : undefined,
    section_key:
      typeof o.section_key === "string"
        ? o.section_key
        : typeof o.sectionKey === "string"
          ? o.sectionKey
          : undefined,
    clause_category:
      typeof o.clause_category === "string"
        ? o.clause_category
        : o.clause_category === null
          ? null
          : typeof o.clauseCategory === "string"
            ? o.clauseCategory
            : o.clauseCategory === null
              ? null
              : undefined,
    posture:
      typeof o.posture === "string"
        ? o.posture
        : o.posture === null
          ? null
          : undefined,
    language: typeof o.language === "string" ? o.language : undefined,
    jurisdiction_applicability:
      typeof o.jurisdiction_applicability === "string"
        ? o.jurisdiction_applicability
        : typeof o.jurisdictionApplicability === "string"
          ? o.jurisdictionApplicability
          : undefined,
    version: typeof o.version === "string" ? o.version : undefined,
  };
}
